/**
 * «Кто видит группу» — панель группового чата по шестерёнке у названия (право `chat_groups_edit`).
 * Группу видят её участники; «Общее <город>» Telegram и MAX по умолчанию — все. Участники: кто писал
 * в группу, кого бот Telegram видит в ней, добавленные руками; убранный руками фоном не возвращается.
 * Спека docs/specs/2026-10-21-chat-group-access-design.md.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RotateCcw, X } from "lucide-react";
import { useState } from "react";

import {
  addGroupMember,
  type GroupAccess as Access,
  type GroupVisibility,
  loadGroupAccess,
  removeGroupMember,
  setGroupVisibility,
} from "@/lib/clientChats";

const SOURCE: Record<string, string> = { author: "писал", telegram: "в группе", manual: "добавлен" };
const MODES: [GroupVisibility, string, string][] = [
  ["members", "Участники", "Группу видят только её участники"],
  ["all", "Все", "Группу видят все, у кого есть «Чаты»"],
];

export function GroupAccess({ chatId }: { chatId: number }) {
  const qc = useQueryClient();
  const [pick, setPick] = useState("");
  const key = ["chat-group", chatId];
  const q = useQuery({ queryKey: key, queryFn: () => loadGroupAccess(chatId) });
  const done = (next: Access) => {
    qc.setQueryData(key, next);
    void qc.invalidateQueries({ queryKey: ["client-chats"] });
  };
  const mode = useMutation({ mutationFn: (v: GroupVisibility | null) => setGroupVisibility(chatId, v), onSuccess: done });
  const add = useMutation({
    mutationFn: (p: { user_id: number; name: string }) => addGroupMember(chatId, p.user_id, p.name),
    onSuccess: (next) => {
      setPick("");
      done(next);
    },
  });
  const remove = useMutation({ mutationFn: (userId: number) => removeGroupMember(chatId, userId), onSuccess: done });
  const failure = q.error ?? mode.error ?? add.error ?? remove.error;
  const g = q.data;
  if (!g) return <p className="px-3 py-3 text-fg-muted">{q.isError ? "Не загрузилось" : "Загружаю…"}</p>;

  const shown = g.members.filter((m) => !m.hidden);
  const hidden = g.members.filter((m) => m.hidden);
  const busy = mode.isPending || add.isPending || remove.isPending;
  const inGroup = new Set(shown.map((m) => m.user_id));
  const free = g.people.filter((p) => !inGroup.has(p.user_id));

  return (
    <div className="space-y-3 px-3 py-3" data-annot="кто видит группу">
      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-3 py-2">
        <div className="mb-1.5 text-[13px] font-medium">Видимость</div>
        <div className="flex rounded-full bg-[var(--tg-input)] p-0.5">
          {MODES.map(([id, label, hint]) => (
            <button
              key={id}
              type="button"
              title={hint}
              disabled={!g.can_edit || busy}
              onClick={() => mode.mutate(id)}
              className={`flex-1 rounded-full px-2 py-1 text-[13px] ${
                g.visibility === id ? "bg-[var(--tg-surface)] font-medium shadow-sm" : "text-[var(--tg-sec)]"
              } ${g.can_edit ? "" : "cursor-default"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="mt-1.5 text-[12px] text-fg-muted">
          {g.custom ? "Задано руками" : "По умолчанию"}: {g.visibility === "all" ? "видят все с «Чатами»" : "видят только участники"}
          {g.custom && g.can_edit && (
            <button
              type="button"
              onClick={() => mode.mutate(null)}
              disabled={busy}
              className="ml-1.5 inline-flex items-center gap-0.5 text-accent-text hover:underline"
              title={`Вернуть умолчание: ${g.default === "all" ? "все" : "участники"}`}
            >
              <RotateCcw size={11} aria-hidden /> вернуть умолчание
            </button>
          )}
        </p>
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-3 py-2">
        <div className="mb-1 flex items-baseline justify-between text-[13px]">
          <span className="font-medium">Участники · {shown.length}</span>
          {g.visibility === "all" && <span className="text-[12px] text-fg-muted">группа открыта всем</span>}
        </div>
        {shown.length === 0 && <p className="text-fg-muted">Пока никого: в группу из кабинета ещё не писали</p>}
        <ul className="divide-y divide-border">
          {shown.map((m) => (
            <li key={m.user_id} className="flex items-center gap-2 py-1">
              <span className="min-w-0 flex-1 truncate">{m.name || `Сотрудник ${m.user_id}`}</span>
              <span className="text-[12px] text-fg-muted">{SOURCE[m.source] ?? m.source}</span>
              {g.can_edit && (
                <button
                  type="button"
                  onClick={() => remove.mutate(m.user_id)}
                  disabled={busy}
                  title="Убрать: группу видеть не будет, фон его не вернёт"
                  className="text-fg-faint hover:text-err"
                >
                  <X size={14} aria-hidden />
                  <span className="sr-only">Убрать {m.name}</span>
                </button>
              )}
            </li>
          ))}
        </ul>
        {g.can_edit && (
          <div className="mt-2 flex gap-1.5">
            <select
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              className="min-w-0 flex-1 rounded-md border border-border bg-bg px-2 py-1 text-[13px]"
              aria-label="Кого добавить"
            >
              <option value="">Добавить сотрудника…</option>
              {free.map((p) => (
                <option key={p.user_id} value={p.user_id}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!pick || busy}
              onClick={() => {
                const p = free.find((x) => String(x.user_id) === pick);
                if (p) add.mutate(p);
              }}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 text-[13px] hover:bg-bg-hover disabled:opacity-50"
            >
              <Plus size={14} aria-hidden /> Добавить
            </button>
          </div>
        )}
        {g.can_edit && hidden.length > 0 && (
          <p className="mt-2 text-[12px] text-fg-muted">
            Убраны руками: {hidden.map((m, i) => (
              <span key={m.user_id}>
                {i > 0 && ", "}
                <button
                  type="button"
                  className="text-accent-text hover:underline"
                  title="Вернуть в участники"
                  onClick={() => add.mutate({ user_id: m.user_id, name: m.name })}
                >
                  {m.name || m.user_id}
                </button>
              </span>
            ))}
          </p>
        )}
      </section>
      {failure && <p className="text-[12.5px] text-err">{failure instanceof Error ? failure.message : String(failure)}</p>}
    </div>
  );
}
